import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  SxProps,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { FC, useEffect, useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'

import dayjs from 'dayjs'
import { nanoid } from 'nanoid'
import ReactQuill from 'react-quill'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { getReadableAnimalDogCategory, getReadableAnimalHealthPathology } from '../../../../domain/Animal/AnimalDisplay'
import {
  CatPathologies,
  DogPathologies,
  FerretPathologies,
  GlobalPathologies,
} from '../../../../domain/Animal/AnimalPathologies'
import {
  AccountDto,
  AccountDtoRoles,
  AnimalDto,
  AnimalDtoSpecies,
  AnimalHealthDtoDogCategory,
  AnimalHealthDtoFIV,
  AnimalHealthDtoFeLV,
  AnimalHealthDtoHasEuropeanPassport,
  AnimalHealthDtoPathologies,
  AnimalHealthTestResultDtoType,
  CreateAnimalBodyDtoVaccinationStatus,
} from '../../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { TrimManager } from '../../../../utils/TrimManager'
import { getColorForBooleanOrUnknown, getReadableBooleanOrUnknown } from '../../../../utils/getReadableBooleanOrUnknown'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { isInEnum } from '../../../../utils/isInEnum'
import { omitEmptyString } from '../../../../utils/omitEmptyString'
import { PATHS } from '../../../PATHS'
import { surfaceCardSx } from '../../../common/surfaceCardSx'
import { colors } from '../../../theme'
import { ColoredChipSelectField } from '../../../common/ColoredChipSelectField'
import { ControlledAutoCompleteWithCustomValue } from '../../../common/ControlledAutocompleteWithCustomValue'
import { ControlledMultipleAutoComplete } from '../../../common/ControlledMultipleAutoComplete'
import { ControlledSelectField } from '../../../common/ControlledSelectField'
import { AnimalCardSaveButton } from '../AnimalCardSaveButton'
import { EditorPrompt } from '../EditorPrompt'

interface Props {
  animal: AnimalDto
  sx?: SxProps<Theme>
  embedded?: boolean
}

type FormParams = {
  vaccinationStatus: CreateAnimalBodyDtoVaccinationStatus | ''
  vaccinationType: string
  firstTCInjectionAt: string // DateString
  secondTCInjectionAt: string // DateString
  thirdTCInjectionAt: string // DateString
  veterinaryChargeAccountId: string | null
  FeLV: AnimalHealthDtoFeLV | ''
  FIV: AnimalHealthDtoFIV | ''
  hasEuropeanPassport?: AnimalHealthDtoHasEuropeanPassport
  dogCategory?: AnimalHealthDtoDogCategory
  pathologies: AnimalHealthDtoPathologies[]
  alimentation?: string
}

export const AnimalHealthCard: FC<Props> = ({ animal, sx, embedded = false }) => {
  const queryParams = new URLSearchParams(location.search)
  const accountsStore = useAccountsStore()
  const navigate = useNavigate()
  const canEditAnimal = useCanEditAnimal()
  const [openSanitaryDialog, setOpenSanitaryDialog] = useState(false)
  const [sanitaryDate, setSanitaryDate] = useState(dayjs().add(3, 'month'))
  const isDesktop = useMediaQuery('(min-width:1100px)')
  const [isExpanded, setIsExpanded] = useState(isDesktop)

  const getDefaultValues = (animal: AnimalDto): FormParams => ({
    vaccinationStatus: animal.vaccination.vaccinationStatus || '',
    vaccinationType: animal.vaccination.vaccinationType || '',
    firstTCInjectionAt: animal.vaccination.firstInjection?.date || '',
    secondTCInjectionAt: animal.vaccination.secondInjection?.date || '',
    thirdTCInjectionAt: animal.vaccination.thirdInjection?.date || '',
    veterinaryChargeAccountId: animal.responsibilities?.veterinaryChargeAccountId || '',
    FeLV: animal.health?.FeLV || '',
    FIV: animal.health?.FIV || '',
    hasEuropeanPassport: animal.health?.hasEuropeanPassport || undefined,
    dogCategory: animal.health?.dogCategory || undefined,
    pathologies: animal.health?.pathologies || [],
    alimentation: animal.health?.alimentation || '',
  })

  const { control, register, handleSubmit, watch, setValue, reset, formState } = useFormExtended(
    animal,
    getDefaultValues
  )

  const options = accountsStore.members.filter((account) => account.roles.includes(AccountDtoRoles.Veterinary))

  const initialVeterinaryOption = options.find(
    (member) => member.id === animal.responsibilities?.veterinaryChargeAccountId
  )

  useEffect(() => {
    setValue('veterinaryChargeAccountId', initialVeterinaryOption?.id || null)
  }, [initialVeterinaryOption?.id])

  const watchVeterinary = watch('veterinaryChargeAccountId')

  const editAnimalMutation = useEditAnimalMutation({})
  const healthFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: { xs: '0.78rem', sm: '0.82rem' },
      fontWeight: 400,
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      fontSize: { xs: '0.72rem', sm: '0.76rem' },
      fontWeight: 400,
    },
    '& .MuiOutlinedInput-input, & .MuiSelect-select, & .MuiAutocomplete-input': {
      fontSize: { xs: '0.92rem', sm: '0.96rem' },
      fontWeight: 400,
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: { xs: '0.92rem', sm: '0.96rem' },
      fontWeight: 300,
      opacity: 1,
    },
  }

  const onSubmit = async (data: Partial<FormParams>) => {
    const newPathologies = data.pathologies?.filter((pathology) => !animal.health?.pathologies?.includes(pathology))
    const deletedPathologies = animal.health?.pathologies?.filter((pathology) => !data.pathologies?.includes(pathology))
    const oldOperations = animal.health?.operations || []
    if (deletedPathologies && deletedPathologies.length > 0) {
      deletedPathologies
        .filter((pathology) => isInEnum(AnimalHealthTestResultDtoType, pathology))
        .forEach((pathology) => {
          oldOperations
            .sort((a, b) => dayjs(b.date, 'DD/MM/YYYY').diff(dayjs(a.date, 'DD/MM/YYYY')))
            .find((operation) => operation.type.includes(pathology as AnimalHealthTestResultDtoType))
            ?.testResults?.forEach((testResult) => {
              if (testResult.type === pathology) {
                testResult.result = 'unknown'
              }
            })
        })
    }
    const newOperations = oldOperations
    if (newPathologies && newPathologies.length > 0) {
      newPathologies
        .filter((pathology) => pathology !== AnimalHealthTestResultDtoType.Coryza)
        .filter((pathology) => isInEnum(AnimalHealthTestResultDtoType, pathology))
        .forEach((pathology) => {
          newOperations.push({
            id: nanoid(),
            date: '',
            type: [pathology as AnimalHealthTestResultDtoType],
            testResults: [
              {
                type: pathology as AnimalHealthTestResultDtoType,
                result: 'positive',
              },
            ],
          })
        })
    }

    const update = omitEmptyString({
      ...animal,
      health: {
        ...animal.health,
        FIV: data.FIV as AnimalHealthDtoFIV,
        FeLV: data.FeLV as AnimalHealthDtoFeLV,
        hasEuropeanPassport: data.hasEuropeanPassport,
        dogCategory: animal.species === AnimalDtoSpecies.Dog ? data.dogCategory : undefined,
        pathologies: data.pathologies,
        operations: newOperations,
        alimentation: data.alimentation,
      },
      responsibilities: {
        ...animal.responsibilities,
        veterinaryChargeAccountId:
          data.veterinaryChargeAccountId || animal.responsibilities?.veterinaryChargeAccountId || undefined,
      },
    })

    await editAnimalMutation.mutateAsync(update)
  }

  const onEditHealthHistory = async (newHealthHistory: string) => {
    const update = { ...animal, health: { ...animal.health, healthHistory: newHealthHistory } }
    await editAnimalMutation.mutate(update)
  }

  const handleSanitarySurveillance = async () => {
    const update = {
      ...animal,
      health: {
        ...animal.health,
        sanitarySurveillanceUntil: sanitaryDate.toISOString(),
      },
    }
    await editAnimalMutation.mutateAsync(update)
    setOpenSanitaryDialog(false)
  }

  const handleRemoveSurveillance = async () => {
    const update = {
      ...animal,
      health: {
        ...animal.health,
        sanitarySurveillanceUntil: undefined,
      },
    }
    await editAnimalMutation.mutateAsync(update)
  }

  const getOptionLabel = (option: AccountDto): string => {
    let optionLabel = fullName(option)
    if (option.id === accountsStore.connectedAccount?.id) {
      optionLabel = `Moi (${optionLabel})`
    }

    return optionLabel
  }

  useEffect(() => {
    if (queryParams.get('tab') === 'sante' && queryParams.get('card') === 'animalHealthCard') {
      const newVeterinary = queryParams.get('veterinary')

      if (newVeterinary !== undefined && newVeterinary !== null) {
        setValue('veterinaryChargeAccountId', newVeterinary || '')
        handleSubmit(onSubmit)()
      }
    }
  }, [])

  useEffect(() => {
    if (watchVeterinary === 'addAccount') {
      reset({ veterinaryChargeAccountId: null })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=sante&role=${AccountDtoRoles.Veterinary}&card=animalHealthCard`
      )
    }
  }, [watchVeterinary])

  const allPathologies = Object.values(AnimalHealthDtoPathologies)

  function filterPathologiesForSpecies(animalSpecies: AnimalDtoSpecies) {
    let filterOutPathologies: Partial<AnimalHealthDtoPathologies[]> = []
    switch (animalSpecies) {
      case AnimalDtoSpecies.Dog:
        filterOutPathologies = CatPathologies.concat(FerretPathologies).filter(
          (pathology) => !DogPathologies.includes(pathology)
        )
        break
      case AnimalDtoSpecies.Cat:
        filterOutPathologies = DogPathologies.concat(FerretPathologies).filter(
          (pathology) => !CatPathologies.includes(pathology)
        )
        break
      default:
        filterOutPathologies = DogPathologies.concat(CatPathologies)
          .concat(FerretPathologies)
          .filter((pathology) => !GlobalPathologies.includes(pathology))
        break
    }

    // Include all operation types except those specifically filtered out for the species
    return allPathologies.filter((type) => !filterOutPathologies.includes(type))
  }

  const speciesPathologies = filterPathologiesForSpecies(animal.species)

  useEffect(() => {
    setIsExpanded(isDesktop)
  }, [isDesktop])

  const content = (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {embedded ? null : (
        <CardHeader
          sx={{
            '& .MuiCardHeader-action': {
              alignSelf: 'center',
              mt: 0,
              mb: 0,
            },
          }}
          title="🏥 Santé"
          titleTypographyProps={{ sx: { fontSize: { xs: 15.5, sm: 16 }, fontWeight: 700, lineHeight: 1.2 } }}
          action={
            !isDesktop ? (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setIsExpanded((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setIsExpanded((prev) => !prev)
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  cursor: 'pointer',
                  color: colors.cello,
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 800, lineHeight: 1.2 }}>
                  Consulter
                </Typography>
                <Box sx={{ ml: -0.25, display: 'flex', alignItems: 'center' }}>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </Box>
            ) : null
          }
        />
      )}
      <Collapse in={embedded ? true : isExpanded} timeout="auto" unmountOnExit={!isDesktop && !embedded}>
        <CardContent
          sx={{
            height: '100%',
            px: embedded ? 0 : undefined,
            pt: embedded ? 0 : undefined,
            pb: embedded ? '0 !important' : undefined,
            ...healthFieldSx,
          }}
        >
          <Grid container spacing={{ md: 1, xs: 1.5 }} alignItems="flex-start">
            <Grid item xs={12} sm={3}>
              <ControlledAutoCompleteWithCustomValue
                sx={healthFieldSx}
                control={control}
                fieldName={'veterinaryChargeAccountId'}
                defaultValue=""
                size="small"
                options={[
                  { value: 'addAccount', label: 'Ajouter un vétérinaire' },
                  ...(accountsStore.connectedAccount &&
                  accountsStore.connectedAccount?.roles.includes(AccountDtoRoles.Veterinary)
                    ? [
                        {
                          value: accountsStore.connectedAccount?.id,
                          label: getOptionLabel(accountsStore.connectedAccount),
                        },
                      ]
                    : []),
                  ...options
                    .filter((option) => option.id !== accountsStore.connectedAccount?.id)
                    .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                    .map((option) => ({
                      label: getOptionLabel(option),
                      value: option.id,
                    })),
                ]}
                label="Vétérinaire en charge"
                error={undefined}
                requiredRule={undefined}
                highlight
              />
            </Grid>

            <Grid item xs={12} sm={3.5}>
              <Box
                sx={{
                  borderLeft: '2px solid',
                  borderLeftColor: 'grey.200',
                  pl: { xs: 1.5, sm: 2 },
                  boxSizing: 'border-box',
                  minHeight: { sm: 40 },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.76rem', sm: '0.8rem' },
                    fontWeight: 400,
                    color: colors.bermudaGray,
                    lineHeight: 1.25,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                  title={options.find((member) => member.id === watchVeterinary)?.email || ''}
                >
                  Email : {options.find((member) => member.id === watchVeterinary)?.email || '-'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={5.5}>
              {animal.species === AnimalDtoSpecies.Dog && (
                <ControlledSelectField
                  sx={healthFieldSx}
                  control={control}
                  fieldName="dogCategory"
                  label="Chien de catégorie"
                  options={Object.values(AnimalHealthDtoDogCategory).map((value: AnimalHealthDtoDogCategory) => ({
                    label: getReadableAnimalDogCategory(value),
                    value: value,
                  }))}
                  size="small"
                  error={undefined}
                  requiredRule=""
                />
              )}
              {/* {animal.species === AnimalDtoSpecies.Cat && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mx: { md: 2, xs: 0 }, gap: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        borderLeft: '2px grey solid',
                        borderLeftColor: 'grey.200',
                        paddingLeft: 1,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Typography
                        variant={'body2'}
                        color="textSecondary"
                        sx={{
                          lineHeight: '0.875rem',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        Test FeLV
                      </Typography>
                      <Chip
                        label={animal.health?.FeLV ? getReadableAnimalHealthTestStatus(animal.health?.FeLV) : 'Inconnu'}
                        sx={{
                          backgroundColor: setOpacity(
                            animal.health?.FeLV ? getColorForAnimalHealthTestStatus(animal.health?.FeLV) : colors.amber,
                            0.8
                          ),
                          color: 'white',
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        borderLeft: '2px grey solid',
                        borderLeftColor: 'grey.200',
                        paddingLeft: 1,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Typography
                        variant={'body2'}
                        color="textSecondary"
                        sx={{
                          lineHeight: '0.875rem',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        Test FIV
                      </Typography>
                      <Chip
                        label={animal.health?.FIV ? getReadableAnimalHealthTestStatus(animal.health?.FIV) : 'Inconnu'}
                        sx={{
                          backgroundColor: setOpacity(
                            animal.health?.FIV ? getColorForAnimalHealthTestStatus(animal.health?.FIV) : colors.amber,
                            0.8
                          ),
                          color: 'white',
                        }}
                      />
                    </Box>
                  </Box>
                )} */}
            </Grid>

            <Grid item xs={12} sm={3}>
              <ColoredChipSelectField
                sx={healthFieldSx}
                control={control}
                fieldName="hasEuropeanPassport"
                label="Détention d'un passeport européen"
                options={Object.values(AnimalHealthDtoHasEuropeanPassport).map(
                  (value: AnimalHealthDtoHasEuropeanPassport) => ({
                    label: getReadableBooleanOrUnknown(value),
                    color: getColorForBooleanOrUnknown(value),
                    value: value,
                  })
                )}
                size="small"
                error={undefined}
                requiredRule=""
              />
            </Grid>

            <Grid item xs={12} sm={9}>
              <ControlledMultipleAutoComplete
                control={control}
                fieldName="pathologies"
                label="Pathologies"
                options={speciesPathologies
                  .sort((a, b) =>
                    getReadableAnimalHealthPathology(a).localeCompare(getReadableAnimalHealthPathology(b))
                  )
                  .map((value: AnimalHealthDtoPathologies) => ({
                    label: getReadableAnimalHealthPathology(value),
                    value: value,
                  }))}
                size="small"
                error={undefined}
                requiredRule={undefined}
                sx={{
                  ...healthFieldSx,
                  // Match the compact height of the passport field, while keeping chip rows usable when expanded.
                  '& .MuiOutlinedInput-root': { minHeight: 40, borderRadius: 2 },
                  '& .MuiOutlinedInput-input': { py: 1.1 },
                  '& .MuiAutocomplete-inputRoot': { alignItems: 'center' },
                }}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              {...register('alimentation')}
              label="Alimentation"
              multiline
              fullWidth
              size="small"
              sx={{ ...healthFieldSx, mt: 1.5, mb: 2 }}
            />
          </Grid>

          {animal.health?.sanitarySurveillanceUntil && (
            <Box
              sx={{
                backgroundColor: 'rgba(105, 141, 172, 0.14)',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                mb: 2,
                border: '1px solid',
                borderColor: 'rgba(105, 141, 172, 0.42)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 0.35, sm: 1 },
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography variant="h6" sx={{ color: colors.cello, fontWeight: 900, lineHeight: 1.1 }}>
                    🦠 En surveillance sanitaire
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.cello,
                      opacity: 0.9,
                      lineHeight: 1.1,
                      textAlign: 'center',
                      flex: 1,
                    }}
                  >
                    jusqu&apos;au <strong>{dayjs(animal.health.sanitarySurveillanceUntil).format('DD/MM/YY')}</strong>
                  </Typography>
                </Box>
                {canEditAnimal && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRemoveSurveillance}
                    disabled={editAnimalMutation.isLoading}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '999px',
                      minHeight: 36,
                      px: 2.25,
                      fontWeight: 800,
                      fontSize: 13,
                      lineHeight: 1.15,
                      textAlign: 'center',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: { xs: '100%', sm: 'none' },
                      minWidth: { xs: '100%', sm: 'auto' },
                      backgroundColor: 'rgba(255, 255, 255, 0.72)',
                      borderColor: 'rgba(122, 129, 151, 0.24)',
                      color: colors.cello,
                      '&:hover': { backgroundColor: 'white', borderColor: 'rgba(122, 129, 151, 0.35)' },
                    }}
                  >
                    Arrêter la surveillance
                  </Button>
                )}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              width: '100%',
              mt: { xs: 2, sm: 0 },
              mb: 2,
              gap: { xs: 1.25, sm: 1.5 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
              {!animal.health?.sanitarySurveillanceUntil && (
                <Button
                  variant="outlined"
                  color="secondary"
                  style={{ textTransform: 'none' }}
                  onClick={() => setOpenSanitaryDialog(true)}
                  disabled={!canEditAnimal}
                  sx={{
                    borderRadius: '999px',
                    minHeight: 36,
                    px: 2.25,
                    fontWeight: 800,
                    fontSize: 13,
                    lineHeight: 1.15,
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                  }}
                >
                  🦠 Mettre en surveillance sanitaire
                </Button>
              )}
            </Box>
            <AnimalCardSaveButton
              isLoading={editAnimalMutation.isLoading}
              disabled={!formState.isDirty}
              sx={{
                borderRadius: '999px',
                minHeight: 36,
                px: 2.25,
                fontWeight: 800,
                fontSize: 13,
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: 'rgba(255, 255, 255, 0.72)',
                borderColor: 'rgba(122, 129, 151, 0.24)',
                color: colors.cello,
                '&:hover': { backgroundColor: 'white', borderColor: 'rgba(122, 129, 151, 0.35)' },
              }}
            />
          </Box>

          <Box
            sx={{
              marginTop: { xs: 4, sm: 6 },
              marginBottom: { xs: 3, sm: 4 },
            }}
          >
            <Card
              sx={{
                ...surfaceCardSx,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.92rem', sm: '0.98rem' },
                      fontWeight: 900,
                      color: colors.cello,
                      lineHeight: 1.2,
                    }}
                  >
                    Suivi et historique de santé
                  </Typography>
                  {canEditAnimal && TrimManager.needTrim(animal.health.healthHistory) && (
                    <EditorPrompt
                      title="Suivi et historique de santé"
                      initialValue={animal.health.healthHistory || ''}
                      onSubmit={onEditHealthHistory}
                      renderButton={(onClick: () => void): JSX.Element => (
                        <Button
                          onClick={onClick}
                          variant="outlined"
                          sx={{
                            textTransform: 'none',
                            borderRadius: '999px',
                            minHeight: { xs: 32, sm: 36 },
                            px: { xs: 1.5, sm: 2.25 },
                            fontWeight: 800,
                            fontSize: 12,
                            alignSelf: { xs: 'flex-start', sm: 'auto' },
                            backgroundColor: 'rgba(255, 255, 255, 0.72)',
                            borderColor: 'rgba(122, 129, 151, 0.24)',
                            color: colors.cello,
                            '&:hover': { backgroundColor: 'white', borderColor: 'rgba(122, 129, 151, 0.35)' },
                          }}
                        >
                          Voir plus
                        </Button>
                      )}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    maxHeight: { xs: 260, md: 320 },
                    overflow: 'auto',
                    borderRadius: 2.5,
                    border: 'none',
                    backgroundColor: '#FFFFFF',
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <ReactQuill
                    value={
                      animal.health.healthHistory && animal.health.healthHistory.length > 520
                        ? `${animal.health.healthHistory.substring(0, 520)}...`
                        : animal.health.healthHistory || '...'
                    }
                    readOnly
                    theme="bubble"
                  />
                  {canEditAnimal && !TrimManager.needTrim(animal.health.healthHistory) && (
                    <EditorPrompt
                      initialValue={animal.health.healthHistory || ''}
                      onSubmit={onEditHealthHistory}
                      title="🏥 Suivi Santé"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Collapse>
    </form>
  )

  return (
    <>
      {embedded ? (
        <Box id="health" sx={sx}>
          {content}
        </Box>
      ) : (
        <Card
          id="health"
          sx={(sx ? [surfaceCardSx, sx, { height: '100%' }] : [surfaceCardSx, { height: '100%' }]) as SxProps<Theme>}
        >
          {content}
        </Card>
      )}

      <Dialog open={openSanitaryDialog} onClose={() => setOpenSanitaryDialog(false)}>
        <DialogTitle>🦠 Mise en surveillance sanitaire</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Sélectionnez la date de fin de surveillance sanitaire pour cet animal.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
              <DatePicker
                label="Date de fin de surveillance"
                value={sanitaryDate}
                onChange={(newValue) => newValue && setSanitaryDate(newValue)}
                format="DD/MM/YYYY"
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: `Par défaut: ${dayjs().add(3, 'month').format('DD/MM/YYYY')} (dans 3 mois)`,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSanitaryDialog(false)}>Annuler</Button>
          <Button
            onClick={handleSanitarySurveillance}
            variant="contained"
            color="warning"
            sx={{ color: 'white' }}
            disabled={editAnimalMutation.isLoading}
          >
            Confirmer la surveillance
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
